import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import * as yup from "yup";

import { Col, Container, Content, Subtitle, Title } from "../styles";
import { Form, ItensRequirements, ListRequirements } from "./styles";

import { Button, Input } from "../../../shared/components";

import alertIcon from "../../../assets/alert-outline.svg";
import checkIcon from "../../../assets/check-icon.svg";

import { onboardGogogoV2, updateOnboardFieldRequest } from "../../../store/modules/register/actions";

import { CD_CHANNEL_TRADEMAP } from "../../../constant/index.const";
import { useAdjust } from "../../../hook/useAdjust";
import RecaptchaInvisible from "../../../components/Recaptcha/RecaptchaInvisible";

const schema = yup.object({
	name: yup
		.string()
		.required("Verifique o seu nome")
		.matches(/^[A-Za-zÀ-ú]{2,} [ A-Za-zÀ-ú]+$/, "Informe seu nome completo"),
	password: yup
		.string()
		.required("Senha é obrigatória")
		.min(8, "")
		.matches(/[a-z]/, `${"\u200B"}`)
		.matches(/[A-Z]/, `${"\u200B"}`)
		.matches(/\D/, `${"\u200B"}`)
		.matches(/[^A-Za-z0-9]/, `${"\u200B"}`)
		.matches(/^(?=.*\d)(?!.*(?:012|123|234|345|456|567|678|789|890))/, `${"\u200B"}`),
	password_confirm: yup
		.string()
		.oneOf([yup.ref("password")], "As senhas devem coincidir")
		.required("Confirme sua senha"),
});

export const Enrollment = () => {
	const dispatch = useDispatch();

	const { initialOnboardingResponseData, isTermAccepted } = useSelector((state) => state.register);

	const { adid } = useAdjust();

	const [tokenRecaptcha, setTokenRecaptcha] = useState({});
	const [disable, setDisable] = useState(true);
	const [hidePass, setHidePass] = useState({
		new: true,
		confirm: true,
	});

	const {
		formState: { errors },
		register,
		handleSubmit,
		watch,
		getValues,
		trigger,
	} = useForm({
		resolver: yupResolver(schema),
		mode: "onChange",
		reValidateMode: "onBlur",
	});

	const { password, password_confirm } = watch();

	useEffect(() => {
		const { unsubscribe } = watch((value) => {
			const isEmpty = Object.values(value).some(
				(item) => item === "" || item === null || item == undefined
			);

			setDisable(isEmpty);

			if (!isEmpty && value.password === value.password_confirm) {
				_updateField();
			}
		});

		return () => unsubscribe();
	}, []);

	useEffect(() => {
		if (password && password_confirm.length) trigger("password_confirm");
		if (password_confirm && password.length) trigger("password");
	}, [password, password_confirm]);

	const handleVisibilityPassword = useCallback(
		/**
		 * @param { "new" | "confirm"} field
		 */
		(field) => {
			setHidePass((prev) => ({
				...prev,
				[field]: !prev[field],
			}));
		},
		[]
	);

	const createAccount = () => {
		dispatch(
			onboardGogogoV2({
				cd_access_token: initialOnboardingResponseData.cd_access_token,
				cd_channel: CD_CHANNEL_TRADEMAP,
				is_term_accepted: isTermAccepted,
				event: "login_after_onboard",
				adid,
				password,
				tokenRecaptcha,
			})
		);
	};

	const _updateField = async () => {
		Object.entries(getValues())
			.filter(([key]) => key !== "password_confirm")
			.forEach(([key, value]) => {
				let extra = null;

				dispatch(updateOnboardFieldRequest(key, value, extra));
			});
	};

	return (
		<Container>
			<Content>
				<Col>
					<Title>Criar uma conta</Title>
					<Subtitle>
						Finalize o seu cadastro, leva menos de{" "}
						<span style={{ color: "var(--brand-primary)" }}>
							um minuto!
						</span>
					</Subtitle>
				</Col>

				<Form onSubmit={handleSubmit(createAccount)}>
					<Col className="gap-3 mb-3">
						<Input
							label="Nome Completo"
							fieldRegister="name"
							placeholder="Informe o seu nome"
							register={register}
							required={true}
							errorMessage={errors.name}
							autoComplete="new-password"
						/>
						<Input
							label="Senha"
							fieldRegister="password"
							placeholder="Insira sua senha"
							register={register}
							required={true}
							type={hidePass.new ? "password" : "text"}
							errorMessage={errors.password}
							autoComplete="new-password"
						>
							<Button
								btnType="icon"
								type="button"
								icons={
									hidePass.new
										? "MdVisibility"
										: "MdVisibilityOff"
								}
								onClick={() =>
									handleVisibilityPassword(
										"new"
									)
								}
								tabIndex={-1}
							/>
						</Input>

						<CheckControl
							password={
								password && password.length
									? password
									: ""
							}
						/>

						<Input
							label="Confirme a Senha"
							fieldRegister="password_confirm"
							placeholder="Insira sua senha"
							register={register}
							required={true}
							type={hidePass.confirm ? "password" : "text"}
							errorMessage={errors.password_confirm}
						>
							<Button
								btnType="icon"
								type="button"
								icons={
									hidePass.confirm
										? "MdVisibility"
										: "MdVisibilityOff"
								}
								onClick={() =>
									handleVisibilityPassword(
										"confirm"
									)
								}
								tabIndex={-1}
							/>
						</Input>
					</Col>
					<Button
						title="Criar conta"
						type="submit"
						disabled={disable || Object.keys(errors).length}
						style={{ marginTop: "1rem" }}
					/>
				</Form>
			</Content>
			<RecaptchaInvisible token={setTokenRecaptcha} change={() => {}} />
		</Container>
	);
};

const CheckControl = ({ password }) => {
	const requirements = useMemo(
		() => [
			{ label: "8 caracteres", test: (value) => value && value.length >= 8 },
			{ label: "1 letra minúscula", test: (value) => /[a-z]/.test(value) },
			{ label: "1 letra maiúscula", test: (value) => /[A-Z]/.test(value) },
			{ label: "1 número", test: (value) => /\d/.test(value) },
			{ label: "1 caractere especial", test: (value) => /[^A-Za-z0-9]/.test(value) },
			{
				label: "A senha não pode ter sequência númerica. Ex: 123, 234.",
				test: (value) =>
					/^(?=.*\d)(?!.*(?:012|123|234|345|456|567|678|789|890))/.test(value),
			},
		],
		[]
	);

	const list = useMemo(
		() => requirements.map((item) => ({ ...item, isValid: item.test(password) })),
		[password, requirements]
	);

	return (
		<ListRequirements>
			{list.map((req, index) => {
				return (
					<ItensRequirements key={index}>
						<img
							src={req.isValid ? checkIcon : alertIcon}
							alt={req.isValid ? "check icon" : "alert icon"}
						/>
						{req.label}
					</ItensRequirements>
				);
			})}
		</ListRequirements>
	);
};

export default Enrollment;
